import { useRef, useState } from "react";
import { Download, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const value = String(reader.result || "");
      resolve(value.includes(",") ? value.split(",", 2)[1] : value);
    };

    reader.onerror = () => {
      reject(new Error("Gagal membaca file ZIP"));
    };

    reader.readAsDataURL(file);
  });
}

export default function MasterDataTransfer({
  kind,
  label,
  onImported,
}) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);

  async function exportZip() {
    setBusy(true);

    try {
      const response = await http.get(
        `/admin/master-data/${kind}/export`,
        { responseType: "blob" },
      );

      const disposition =
        response.headers?.["content-disposition"] || "";

      const match = disposition.match(
        /filename="?([^"]+)"?/i,
      );

      const filename =
        match?.[1] ||
        `${kind}-backup.zip`;

      const url = URL.createObjectURL(
        new Blob([response.data], {
          type: "application/zip",
        }),
      );

      const link =
        document.createElement("a");

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success(
        `Export ${label} berhasil`,
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  function openImport() {
    setFile(null);
    setMode("append");
    setOpen(true);
  }

  async function importZip() {
    if (!file) {
      toast.error("Pilih file ZIP terlebih dahulu.");
      return;
    }

    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("File import harus berformat ZIP.");
      return;
    }

    setBusy(true);

    try {
      const fileBase64 =
        await readFileBase64(file);

      const { data } = await http.post(
        `/admin/master-data/${kind}/import`,
        {
          mode,
          file_base64: fileBase64,
        },
      );

      toast.success(
        `Import selesai: ${data.added || 0} masuk, ${data.skipped || 0} dilewati`,
      );

      setOpen(false);
      setFile(null);

      if (onImported) {
        await onImported();
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={exportZip}
        className="flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2.5 text-sm font-bold text-card-foreground disabled:opacity-50"
        title={`Export ${label}`}
      >
        <Download size={15} />
        Export
      </button>

      <button
        type="button"
        disabled={busy}
        onClick={openImport}
        className="flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2.5 text-sm font-bold text-card-foreground disabled:opacity-50"
        title={`Import ${label}`}
      >
        <Upload size={15} />
        Import
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Tutup"
            onClick={() => !busy && setOpen(false)}
            className="absolute inset-0 bg-black/65 backdrop-blur-sm"
          />

          <div className="relative w-full max-w-md rounded-3xl border border-border bg-card p-5 shadow-2xl">
            <button
              type="button"
              disabled={busy}
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-muted disabled:opacity-50"
            >
              <X size={17} />
            </button>

            <div className="pr-12">
              <h2 className="font-heading text-xl font-extrabold text-card-foreground">
                Import {label}
              </h2>

              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                Pilih file ZIP yang sebelumnya dibuat dari tombol Export di halaman {label}.
              </p>
            </div>

            <input
              ref={inputRef}
              type="file"
              accept=".zip,application/zip"
              hidden
              onChange={(e) => {
                setFile(
                  e.target.files?.[0] || null,
                );
                e.target.value = "";
              }}
            />

            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="mt-5 w-full rounded-2xl border border-dashed border-border bg-muted/50 p-4 text-left disabled:opacity-50"
            >
              <div className="text-sm font-bold text-card-foreground">
                {file
                  ? file.name
                  : "Pilih file ZIP"}
              </div>

              <div className="mt-1 text-xs text-muted-foreground">
                {file
                  ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                  : "Klik untuk memilih backup"}
              </div>
            </button>

            <div className="mt-4 space-y-2">
              <label
                className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 ${
                  mode === "append"
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <input
                  type="radio"
                  name={`import-mode-${kind}`}
                  checked={mode === "append"}
                  onChange={() => setMode("append")}
                  className="mt-1"
                />

                <span>
                  <b className="block text-sm text-card-foreground">
                    Tambahkan ke data yang sudah ada
                  </b>
                  <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                    Data lama tetap ada. Data dengan nama yang sama akan dilewati agar tidak dobel.
                  </span>
                </span>
              </label>

              <label
                className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 ${
                  mode === "replace"
                    ? "border-destructive bg-destructive/5"
                    : "border-border"
                }`}
              >
                <input
                  type="radio"
                  name={`import-mode-${kind}`}
                  checked={mode === "replace"}
                  onChange={() => setMode("replace")}
                  className="mt-1"
                />

                <span>
                  <b className="block text-sm text-card-foreground">
                    Ganti semua data {label}
                  </b>
                  <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                    Data {label} yang sekarang akan diganti oleh isi file. Sistem akan menolak jika tindakan ini membuat relasi Produk / Stok / Modifier rusak.
                  </span>
                </span>
              </label>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => setOpen(false)}
                className="rounded-xl bg-muted py-3 text-sm font-bold text-card-foreground disabled:opacity-50"
              >
                Batal
              </button>

              <button
                type="button"
                disabled={busy || !file}
                onClick={importZip}
                className="flex items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground disabled:opacity-50"
              >
                {busy && (
                  <Loader2
                    size={15}
                    className="wm-spin"
                  />
                )}
                Import Sekarang
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
